import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';

import type { Form, FormField } from '../../interfaces/form.interface';
import type {
  DropdownData,
  FilteredValue,
  RepeatableComputeContext,
  RepeatableGroupConfiguration,
  RepeatableSubField,
} from '../../interfaces/input.interface';
import { InputType } from '../../interfaces/input.interface';

type RowValue = Record<string, unknown>;

type PendingDropdownItem = {
  rowIndex: number;
  logicalKey: string;
  rawIds: string[];
  sf: RepeatableSubField;
  isMulti: boolean;
};

const NULLISH_STRING_TOKENS = new Set<string>([
  'null',
  'undefined',
  'nan',
]);

@Component({
  selector: 'app-repeatable-group',
  standalone: false,
  templateUrl: './repeatable-group.component.html',
  styleUrls: ['./repeatable-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepeatableGroupComponent<T> implements OnInit, OnDestroy {
  @Input({ required: true }) public field: FormField<T> | null = null;
  @Input({ required: true }) public model: FormGroup | null = null;
  @Input({ required: true }) public form: Form<T> | null = null;

  @Input() public isEditable = true;

  /** Enable detailed console diagnostics */
  @Input() public debug = false;

  public readonly INPUT = InputType;

  public rowsForm: FormGroup = new FormGroup({});
  public rowsArray: FormArray<FormGroup> = new FormArray<FormGroup>([]);

  private readonly destroyed$ = new Subject<void>();
  private readonly modelBindingsStop$ = new Subject<void>();

  /** Avoid infinite loops between model->rows and rows->model */
  private isInternalSync = false;

  /** Prevent writing empty rows into model before server patch arrives */
  private suspendSyncToModel = true;

  /** Pending IDs to resolve when dropdown options are not ready */
  private pendingDropdown: PendingDropdownItem[] = [];
  private pendingResolveTimer: number | null = null;
  private pendingResolveAttempt = 0;

  private get cfg(): RepeatableGroupConfiguration {
    const cfg = this.field?.input?.repeatableGroupConfiguration;

    if (!cfg) {
      this.debugWarn('Repeatable group configuration is missing. Rendering empty safely.');
      return {
        rows: 1,
        addButtonLabel: 'Add',
        bindings: {},
        serialization: { separator: ';' },
        subFields: [],
      };
    }

    return cfg;
  }

  public ngOnInit(): void {
    this.debugLog('ngOnInit() start', {
      isEditable: this.isEditable,
      bindings: this.cfg.bindings,
      serialization: this.cfg.serialization,
      subFields: (this.cfg.subFields ?? []).map((s) => s.key),
    });

    this.bootstrapFormSafe();

    // Important: listen for parent patchValue AFTER we bootstrap rows
    this.bindModelHydrationPipeline();

    this.hydrateFromModel();
    this.bindRecomputePipeline();

    this.debugLog('ngOnInit() done');
  }

  public ngOnDestroy(): void {
    this.debugLog('ngOnDestroy()');

    if (this.pendingResolveTimer != null) {
      clearTimeout(this.pendingResolveTimer);
      this.pendingResolveTimer = null;
    }

    this.modelBindingsStop$.next();
    this.modelBindingsStop$.complete();

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && !changes['model'].firstChange) {
      this.debugWarn('model reference changed -> rebind + rehydrate');
      this.bindModelHydrationPipeline();
      this.hydrateFromModel();
    }

    if (changes['field'] && !changes['field'].firstChange) {
      this.debugWarn('field changed -> rehydrate');
      this.hydrateFromModel();
    }
  }
  
  // -----------------------------
  // UI helpers
  // -----------------------------

  public visibleSubFields(): RepeatableSubField[] {
    const subFields = this.cfg.subFields ?? [];

    return subFields.filter((sf) => this.safeIsVisible(sf));
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public get currentCount(): number {
    return this.rowsArray?.length ?? 0;
  }

  public get maxRows(): number {
    const max = Number(this.cfg.rows ?? 1);

    return Number.isFinite(max) && max > 0 ? max : 1;
  }

  public canAddRow(): boolean {
    return this.isEditable && this.currentCount < this.maxRows;
  }

  public canDeleteRow(): boolean {
    return this.isEditable && this.currentCount > 1;
  }

  public addRow(): void {
    if (!this.canAddRow()) return;

    this.debugLog('addRow() before', { len: this.rowsArray.length });

    this.rowsArray.push(this.createRowGroupSafe());

    // If user starts editing before model is "ready", allow sync now.
    this.enableSyncIfUserEditing();

    this.recomputeAllAndSync('addRow');

    this.debugLog('addRow() after', { len: this.rowsArray.length });
  }

  public deleteRow(rowIndex: number): void {
    if (!this.isEditable) return;
    if (rowIndex < 0 || rowIndex >= this.currentCount) return;

    this.debugLog('deleteRow()', { rowIndex, beforeLen: this.rowsArray.length });

    this.rowsArray.removeAt(rowIndex);

    if (this.rowsArray.length === 0) {
      this.rowsArray.push(this.createRowGroupSafe());
    }

    this.enableSyncIfUserEditing();
    this.recomputeAllAndSync('deleteRow');

    this.debugLog('deleteRow() done', { afterLen: this.rowsArray.length });
  }

  /** Fix for ONLY_TEXT where 0 is valid value */
  public onlyTextCellText(sf: RepeatableSubField, rowIndex: number): string {
    const ctrl = this.rowsArray.at(rowIndex)?.get(sf.key);
    const raw = ctrl?.value;

    if (raw === null || raw === undefined) return '0,00';

    const s = String(raw).trim();

    if (!s || this.isNullishStringToken(s)) return '0,00';

    return s;
  }

  public readOnlyCellText(sf: RepeatableSubField, rowIndex: number): string {
    const ctrl = this.rowsArray.at(rowIndex)?.get(sf.key);
    const v = ctrl?.value;

    if (v === null || v === undefined) {
      return sf.input?.type === InputType.ONLY_TEXT ? '0,00' : '-';
    }

    if (typeof v === 'string') {
      const t = v.trim();

      if (!t || this.isNullishStringToken(t)) {
        return sf.input?.type === InputType.ONLY_TEXT ? '0,00' : '-';
      }

      // Info view can contain labels directly
      if (this.isInfoLabelValue(sf, t)) {
        return this.normalizeLabelText(t) || '-';
      }
    }

    switch (sf.input?.type) {
      case InputType.DROPDOWN:
      case InputType.DROPDOWN_SEARCH:
        return this.resolveDropdownLabel(sf, v) ?? '-';

      case InputType.MULTISELECT:
      case InputType.MULTISELECT_SEARCH:
        return this.resolveMultiLabels(sf, v) ?? '-';

      default:
        return String(v);
    }
  }

  public debugDump(): void {
    if (!this.debug) return;

    this.debugLog('debugDump()', {
      isEditable: this.isEditable,
      maxRows: this.maxRows,
      suspendSyncToModel: this.suspendSyncToModel,
      rows: this.getRowsSnapshot(),
      pendingDropdown: this.pendingDropdown,
      modelValue: this.model?.getRawValue?.() ?? null,
    });
  }

  // -----------------------------
  // Model -> rows hydration (critical)
  // -----------------------------

  /**
   * Rehydrate whenever parent patches the bound model controls.
   * Also: determine when model is "ready" to allow rows->model sync.
   */
  private bindModelHydrationPipeline(): void {
    if (!this.model) {
      this.debugWarn('bindModelHydrationPipeline(): model missing.');
      return;
    }

    const entries = Object.entries(this.cfg.bindings ?? {});
    if (entries.length === 0) return;

    // stop previous if any
    this.modelBindingsStop$.next();

    for (const [logicalKey, targetControlName] of entries) {
      const ctrl = this.model.get(targetControlName);

      if (!ctrl) {
        this.debugWarn('Model control missing for binding.', { logicalKey, targetControlName });
        continue;
      }

      ctrl.valueChanges
        .pipe(auditTime(0), takeUntil(this.destroyed$), takeUntil(this.modelBindingsStop$))
        .subscribe((v: unknown) => {
          if (this.isInternalSync) return;

          if (this.suspendSyncToModel && this.hasNonEmptyValue(v)) {
            this.suspendSyncToModel = false;

            this.debugLog('Model became ready -> enabling sync to model.', {
              targetControlName,
              value: v,
            });
          }

          this.debugLog('External model change -> rehydrateFromModel()', {
            targetControlName,
            value: v,
          });

          this.hydrateFromModel();
        });
    }

    // If model already has values at init, allow sync immediately
    if (this.isAnyBindingNonEmpty(entries)) {
      this.suspendSyncToModel = false;
      this.debugLog('Model already ready at init -> enabling sync to model.');
    }
  }

  private isAnyBindingNonEmpty(entries: Array<[string, string]>): boolean {
    if (!this.model) return false;

    return entries.some(([, ctrlName]) => this.hasNonEmptyValue(this.model?.get(ctrlName)?.value));
  }

  private hasNonEmptyValue(v: unknown): boolean {
    if (v === null || v === undefined) return false;

    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s !== '' && !this.isNullishStringToken(s);
    }

    if (Array.isArray(v)) return v.length > 0;

    return true;
  }

  // -----------------------------
  // Form creation
  // -----------------------------

  private bootstrapFormSafe(): void {
    this.rowsArray = new FormArray<FormGroup>([]);
    this.rowsArray.push(this.createRowGroupSafe());

    this.rowsForm = new FormGroup({
      rows: this.rowsArray,
    });
  }

  private createRowGroupSafe(): FormGroup {
    const group: Record<string, AbstractControl> = {};
    const subFields = this.cfg.subFields ?? [];

    for (const sf of subFields) {
      // Must accept objects for dropdowns, arrays for multi, etc.
      group[sf.key] = new FormControl<unknown>(null, { nonNullable: false });
    }

    return new FormGroup(group);
  }

  // -----------------------------
  // rows -> recompute/sync pipeline
  // -----------------------------

  private bindRecomputePipeline(): void {
    this.rowsArray.valueChanges
      .pipe(auditTime(0), takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.isInternalSync) return;

        // user is editing => allow sync even if server hasn't patched yet
        this.enableSyncIfUserEditing();

        this.debugLog('rowsArray.valueChanges -> recomputeAllAndSync()', this.getRowsSnapshot());
        this.recomputeAllAndSync('rowsArray.valueChanges');
      });
  }

  private enableSyncIfUserEditing(): void {
    if (!this.isEditable) return;

    if (this.suspendSyncToModel) {
      this.suspendSyncToModel = false;
      this.debugWarn('Sync to model enabled due to user editing before model became ready.');
    }
  }

  private recomputeAllAndSync(source: string): void {
    this.recomputeAll(source);
    this.syncRowsToModel(source);
  }

  private recomputeAll(source: string): void {
    const subFields = this.cfg.subFields ?? [];
    const rowsForCompute = this.getRowsSnapshotAsStrings();

    for (let rowIndex = 0; rowIndex < this.rowsArray.length; rowIndex++) {
      const rowGroup = this.rowsArray.at(rowIndex);

      for (const sf of subFields) {
        if (typeof sf.computeValue !== 'function') continue;

        const ctx: RepeatableComputeContext = {
          rowIndex,
          rows: rowsForCompute,
          model: this.model ?? new FormGroup({}),
        };

        let computed: string;

        try {
          computed = sf.computeValue(ctx);
        } catch (err: unknown) {
          this.debugWarn('computeValue() threw. Skipping patch.', { source, rowIndex, key: sf.key, err });
          continue;
        }

        const control = rowGroup.get(sf.key);
        if (!control) continue;

        let row = rowsForCompute[rowIndex];
        if (!this.areEqualLoose(control.value, computed) && row) {
          control.patchValue(computed, { emitEvent: false });
          row[sf.key] = String(computed);
        }
      }
    }
  }

  private syncRowsToModel(source: string): void {
    if (!this.model) {
      this.debugWarn('syncRowsToModel(): model missing, skipping.', { source });
      return;
    }

    if (this.suspendSyncToModel) {
      this.debugWarn('syncRowsToModel(): skipped (model not ready).', { source });
      return;
    }

    const { bindings, serialization } = this.cfg;
    const sep = serialization?.separator ?? ';';

    const rows = this.getRowsSnapshot();
    const subFields = this.cfg.subFields ?? [];

    this.isInternalSync = true;

    try {
      for (const [logicalKey, targetControlName] of Object.entries(bindings ?? {})) {
        const sf = subFields.find((x) => x.key === logicalKey);

        const joined = rows
          .map((r) => this.serializeCellValue(r[logicalKey], sf))
          .join(sep);

        const ctrl = this.model.get(targetControlName);

        if (ctrl) {
          const prev = String(ctrl.value ?? '');

          if (prev !== joined) {
            ctrl.patchValue(joined, { emitEvent: true });

            this.debugLog('syncRowsToModel: patched existing model control.', {
              source,
              logicalKey,
              targetControlName,
              prev,
              joined,
            });
          }
        } else {
          this.model.addControl(targetControlName, new FormControl(joined));

          this.debugWarn('syncRowsToModel: model control missing; created it.', {
            source,
            logicalKey,
            targetControlName,
            joined,
          });

          this.model.updateValueAndValidity({ emitEvent: true });
        }
      }
    } finally {
      this.isInternalSync = false;
    }
  }

  // -----------------------------
  // Hydrate from model (robust)
  // -----------------------------

  private hydrateFromModel(): void {
    if (!this.model) {
      this.debugWarn('hydrateFromModel(): model missing.');
      return;
    }

    const { bindings, serialization } = this.cfg;
    const sep = serialization?.separator ?? ';';

    const entries = Object.entries(bindings ?? {});
    if (entries.length === 0) {
      this.debugWarn('hydrateFromModel(): No bindings found.');
      return;
    }

    const splitByControl: Record<string, string[]> = {};
    let maxLen = 0;

    for (const [logicalKey, targetControlName] of entries) {
      const raw = this.model.get(targetControlName)?.value;
      const parts = this.splitSerializedKeepEmptiesAndSanitize(raw, sep);

      splitByControl[logicalKey] = parts;
      maxLen = Math.max(maxLen, parts.length);

      this.debugLog('hydrate split', { logicalKey, targetControlName, raw, parts, partsLen: parts.length });
    }

    const desiredRows = Math.min(Math.max(maxLen, 1), this.maxRows);

    while (this.rowsArray.length < desiredRows) this.rowsArray.push(this.createRowGroupSafe());
    while (this.rowsArray.length > desiredRows) this.rowsArray.removeAt(this.rowsArray.length - 1);

    this.pendingDropdown = [];
    this.pendingResolveAttempt = 0;

    const subFields = this.cfg.subFields ?? [];

    this.isInternalSync = true;

    try {
      for (const [logicalKey] of entries) {
        const sf = subFields.find((f) => f.key === logicalKey);

        if (!sf) {
          this.debugWarn('hydrateFromModel(): subField not found for logicalKey.', { logicalKey });
          continue;
        }

        const values = splitByControl[logicalKey] ?? [];

        for (let rowIndex = 0; rowIndex < this.rowsArray.length; rowIndex++) {
          const row = this.rowsArray.at(rowIndex);
          const ctrl = row.get(logicalKey);

          if (!ctrl) {
            this.debugWarn('hydrate: missing row control.', { rowIndex, logicalKey });
            continue;
          }

          const token = String(values[rowIndex] ?? '').trim();

          if (!token || this.isNullishStringToken(token)) {
            ctrl.patchValue(null, { emitEvent: false });
            continue;
          }

          const type = sf.input?.type;

          const isSelect =
            type === InputType.DROPDOWN ||
            type === InputType.DROPDOWN_SEARCH ||
            type === InputType.MULTISELECT ||
            type === InputType.MULTISELECT_SEARCH;

          // INFO MODE: backend may send labels instead of ids
          if (!this.isEditable && isSelect) {
            const looksLikeLabel = !this.isNumericLike(token);

            if (looksLikeLabel) {
              ctrl.patchValue(token, { emitEvent: false });
              continue;
            }
          }

          // NUMERIC: accept comma decimal for UI type="number"
          if (type === InputType.NUMERIC) {
            const parsed = this.parseFlexibleNumber(token);

            if (parsed === null) {
              this.debugWarn('NUMERIC token not parseable -> input type=number will show blank.', { key: logicalKey, token });
              ctrl.patchValue(null, { emitEvent: false });
            } else {
              ctrl.patchValue(parsed, { emitEvent: false });
            }

            continue;
          }

          // Select-like
          if (isSelect) {
            this.hydrateSelectCell(sf, ctrl, rowIndex, logicalKey, token);
            continue;
          }

          // Default: store as string
          ctrl.patchValue(token, { emitEvent: false });
        }
      }
    } finally {
      this.isInternalSync = false;
    }

    if (this.pendingDropdown.length > 0) {
      this.debugWarn('hydrateFromModel(): pendingDropdown (options not ready or ID filtered out).', {
        pendingCount: this.pendingDropdown.length,
        pending: this.pendingDropdown,
      });

      this.scheduleResolvePending();
    }

    // recompute derived fields and sync if allowed
    this.recomputeAllAndSync('hydrateFromModel');
  }

  private hydrateSelectCell(
    sf: RepeatableSubField,
    ctrl: AbstractControl,
    rowIndex: number,
    logicalKey: string,
    token: string,
  ): void {
    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const type = sf.input?.type;

    const isMulti =
      type === InputType.MULTISELECT ||
      type === InputType.MULTISELECT_SEARCH;

    if (!isMulti) {
      const selectedObj = this.mapIdToDropdownValue(token, sf);

      if (selectedObj) {
        ctrl.patchValue(selectedObj, { emitEvent: false });
        return;
      }

      ctrl.patchValue(null, { emitEvent: false });

      this.logWhySelectCannotRender(sf, rowIndex, token);

      this.pendingDropdown.push({
        rowIndex,
        logicalKey,
        rawIds: [token],
        sf,
        isMulti: false,
      });

      return;
    }

    // MULTISELECT format inside each cell: "1,2,3" (comma) by default
    const ids = this.parseIdsFromToken(token, ',', pk);

    const options = this.dropdownOptions(sf);

    const resolved = ids
      .map((id) => options.find((o) => String(o?.value?.[pk]) === id)?.value ?? null)
      .filter((v): v is unknown => v !== null);

    ctrl.patchValue(resolved, { emitEvent: false });

    const unresolvedIds = ids.filter((id) => !options.some((o) => String(o?.value?.[pk]) === id));

    if (unresolvedIds.length > 0) {
      this.debugWarn('hydrate: multiselect ids partially unresolved.', {
        rowIndex,
        key: logicalKey,
        unresolvedIds,
        optionsLen: options.length,
      });

      this.pendingDropdown.push({
        rowIndex,
        logicalKey,
        rawIds: unresolvedIds,
        sf,
        isMulti: true,
      });
    }
  }

  // -----------------------------
  // Pending resolve when options arrive
  // -----------------------------

  private scheduleResolvePending(): void {
    if (this.pendingResolveTimer != null) {
      clearTimeout(this.pendingResolveTimer);
      this.pendingResolveTimer = null;
    }

    this.pendingResolveTimer = window.setTimeout(() => {
      this.tryResolvePending();
    }, 50);

    this.debugLog('scheduleResolvePending() setTimeout 50ms');
  }

  private tryResolvePending(): void {
    if (this.pendingDropdown.length === 0) return;

    const MAX_ATTEMPTS = 200;
    this.pendingResolveAttempt++;

    const stillPending: PendingDropdownItem[] = [];

    for (const item of this.pendingDropdown) {
      const rowGroup = this.rowsArray.at(item.rowIndex);
      const ctrl = rowGroup?.get(item.logicalKey);

      if (!ctrl) {
        this.debugWarn('tryResolvePending(): control missing.', { item });
        continue;
      }

      const pk = item.sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
      const options = this.dropdownOptions(item.sf);

      if (!item.isMulti) {
        const id = item.rawIds[0] ?? '';
        const selectedObj = this.mapIdToDropdownValue(id, item.sf);

        if (selectedObj) {
          ctrl.patchValue(selectedObj, { emitEvent: false });
        } else {
          stillPending.push(item);
        }

        continue;
      }

      const current = Array.isArray(ctrl.value) ? (ctrl.value as unknown[]) : [];
      const resolvedNow = item.rawIds
        .map((id) => options.find((o) => String(o?.value?.[pk]) === id)?.value ?? null)
        .filter((v): v is unknown => v !== null);

      const next = [...current, ...resolvedNow];

      ctrl.patchValue(next, { emitEvent: false });

      const unresolvedIds = item.rawIds.filter((id) => !options.some((o) => String(o?.value?.[pk]) === id));

      if (unresolvedIds.length > 0) {
        stillPending.push({ ...item, rawIds: unresolvedIds });
      }
    }

    this.pendingDropdown = stillPending;

    if (this.pendingDropdown.length > 0 && this.pendingResolveAttempt < MAX_ATTEMPTS) {
      this.scheduleResolvePending();
      return;
    }

    if (this.pendingDropdown.length > 0) {
      this.debugWarn('tryResolvePending(): gave up unresolved ids', {
        attempts: this.pendingResolveAttempt,
        pending: this.pendingDropdown,
      });
    } else {
      this.debugLog('tryResolvePending(): all resolved');
    }

    this.recomputeAllAndSync('tryResolvePending');
  }

  // -----------------------------
  // Dropdown / labels utilities
  // -----------------------------

  public dropdownOptions(sf: RepeatableSubField): Array<FilteredValue<any>> {
    try {
      const dd = sf.input?.data as DropdownData<any> | undefined;
      const cfg = sf.input?.dropdownConfiguration;

      const filtered = (dd?.filtered ?? []) as Array<FilteredValue<any>>;
      const values = (dd?.values ?? []) as Array<FilteredValue<any>>;

      const filteredHasRealValues = filtered.some((it) => it?.value != null);
      const valuesHasRealValues = values.some((it) => it?.value != null);

      let base: Array<FilteredValue<any>> = [];

      if (cfg?.filteredValues?.length) {
        base = cfg.filteredValues as Array<FilteredValue<any>>;
      } else if (valuesHasRealValues) {
        base = values;
      } else if (filteredHasRealValues) {
        base = filtered;
      } else {
        base = [];
      }

      const filterFn = cfg?.filter as ((it: any) => boolean | undefined) | undefined;

      if (!filterFn) return base;

      return base.filter((it) => {
        try {
          return filterFn(it) !== false;
        } catch (err: unknown) {
          this.debugWarn('dropdownOptions(): filter threw, keeping item by default.', { key: sf.key, err });
          return true;
        }
      });
    } catch (err: unknown) {
      this.debugWarn('dropdownOptions(): unexpected error, returning empty list.', { key: sf.key, err });
      return [];
    }
  }

  private mapIdToDropdownValue(idRaw: unknown, sf: RepeatableSubField): any | null {
    const id = String(idRaw ?? '').trim();
    if (!id || this.isNullishStringToken(id)) return null;

    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const options = this.dropdownOptions(sf);

    const found = options.find((o) => String(o?.value?.[pk]) === id);

    return found ? found.value : null;
  }

  private logWhySelectCannotRender(sf: RepeatableSubField, rowIndex: number, rawId: string): void {
    if (!this.debug) return;

    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const opts = this.dropdownOptions(sf);

    const baseValuesLen = Array.isArray((sf.input?.data as any)?.values) ? (sf.input?.data as any).values.length : 0;
    const baseFilteredLen = Array.isArray((sf.input?.data as any)?.filtered) ? (sf.input?.data as any).filtered.length : 0;

    const reason =
      opts.length === 0
        ? 'Options list is empty (API not loaded yet or filter removed all).'
        : opts.some((o) => String(o?.value?.[pk]) === rawId)
          ? 'ID exists in options but UI still blank (identity mismatch; ensure dataKey matches pk).'
          : 'ID not found in options (filtered out or wrong dataset).';

    this.debugWarn('Select cannot display value.', {
      rowIndex,
      key: sf.key,
      rawId,
      pk,
      optionsLen: opts.length,
      baseValuesLen,
      baseFilteredLen,
      reason,
    });
  }

  // -----------------------------
  // Read-only: labels in info mode
  // -----------------------------

  private isInfoLabelValue(sf: RepeatableSubField, raw: unknown): boolean {
    if (this.isEditable) return false;

    const t = sf.input?.type;

    const isSelect =
      t === InputType.DROPDOWN ||
      t === InputType.DROPDOWN_SEARCH ||
      t === InputType.MULTISELECT ||
      t === InputType.MULTISELECT_SEARCH;

    if (!isSelect) return false;

    if (raw === null || raw === undefined) return false;
    if (typeof raw === 'object') return false;

    const s = String(raw).trim();
    if (!s || this.isNullishStringToken(s)) return false;

    const parts = s.split(';').map((x) => x.trim()).filter((x) => x !== '');
    if (parts.length === 0) return false;

    const allNumericLike = parts.every((p) => this.isNumericLike(p));
    if (allNumericLike) return false;

    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const opts = this.dropdownOptions(sf);

    if (opts.length > 0) {
      const anyPartIsKnownId = parts.some((p) => opts.some((o) => String(o?.value?.[pk]) === p));
      if (anyPartIsKnownId) return false;
    }

    return true;
  }

  private normalizeLabelText(raw: unknown): string {
    const s = String(raw ?? '').trim();
    if (!s) return '';

    const parts = s.split(';').map((x) => x.trim()).filter((x) => x !== '');

    return parts.join(', ');
  }

  // -----------------------------
  // Serialize helpers
  // -----------------------------

  private serializeCellValue(value: unknown, sf?: RepeatableSubField): string {
    if (value === null || value === undefined) return '';

    // Keep info-mode labels string as-is
    if (!this.isEditable && typeof value === 'string') {
      const s = value.trim();

      if (!s || this.isNullishStringToken(s)) return '';

      if (s.includes(';')) return s;

      return s;
    }

    // numeric -> format for backend (comma decimal)
    if (typeof value === 'number') {
      return this.formatNumberForModel(value);
    }

    const pk = sf?.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';

    // multi -> "id1,id2"
    if (Array.isArray(value)) {
      const ids = value
        .map((x) => this.normalizeToId(x, pk))
        .filter((x): x is string => !!x);

      return ids.join(',');
    }

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;

      if (obj['value'] && typeof obj['value'] === 'object') {
        const inner = obj['value'] as Record<string, unknown>;
        const id = inner[pk] ?? inner['id'];
        return this.valueToStringSafe(id);
      }

      const id = obj[pk] ?? obj['id'];
      return this.valueToStringSafe(id);
    }

    return this.valueToStringSafe(value);
  }

  private valueToStringSafe(value: unknown): string {
    if (value === null || value === undefined) return '';

    const s = String(value).trim();

    if (!s || this.isNullishStringToken(s)) return '';

    return s;
  }

  // -----------------------------
  // Read-only label resolution
  // -----------------------------

  private resolveDropdownLabel(sf: RepeatableSubField, raw: unknown): string | null {
    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const ids = this.parseIdsFromToken(raw, ';', pk);

    if (ids.length === 0) return null;

    const labels = this.idsToLabels(sf, ids);

    return labels.length ? labels.join(', ') : null;
  }

  private resolveMultiLabels(sf: RepeatableSubField, raw: unknown): string | null {
    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const ids = this.parseIdsFromToken(raw, ';', pk);

    if (ids.length === 0) return null;

    const labels = this.idsToLabels(sf, ids);

    return labels.length ? labels.join(', ') : null;
  }

  private parseIdsFromToken(raw: unknown, itemSeparator: string, pk: string): string[] {
    if (raw === null || raw === undefined) return [];

    if (Array.isArray(raw)) {
      return raw
        .map((x) => this.normalizeToId(x, pk))
        .filter((x): x is string => !!x);
    }

    if (typeof raw === 'string' || typeof raw === 'number') {
      const s = String(raw).trim();

      if (!s || this.isNullishStringToken(s)) return [];

      return s
        .split(itemSeparator)
        .map((x) => x.trim())
        .filter((x) => x !== '' && !this.isNullishStringToken(x));
    }

    const one = this.normalizeToId(raw, pk);

    return one ? [one] : [];
  }

  private idsToLabels(sf: RepeatableSubField, ids: string[]): string[] {
    const pk = sf.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
    const opts = this.dropdownOptions(sf);

    return ids
      .map((id) => opts.find((o) => String(o?.value?.[pk]) === id)?.label ?? '')
      .filter((l) => l.trim() !== '');
  }

  private normalizeToId(raw: unknown, pk: string): string | null {
    if (raw === null || raw === undefined) return null;

    if (typeof raw === 'string' || typeof raw === 'number') {
      const s = String(raw).trim();

      if (!s || this.isNullishStringToken(s)) return null;

      return s;
    }

    if (typeof raw === 'object') {
      const obj = raw as any;

      if (obj.value && typeof obj.value === 'object') {
        const id = obj.value?.[pk] ?? obj.value?.id;
        return id != null ? String(id).trim() : null;
      }

      const id = obj?.[pk] ?? obj?.id;
      return id != null ? String(id).trim() : null;
    }

    return null;
  }

  // -----------------------------
  // Numeric parsing/formatting
  // -----------------------------

  private parseFlexibleNumber(token: string): number | null {
    const t = token.trim();
    if (!t || this.isNullishStringToken(t)) return null;

    const lastComma = t.lastIndexOf(',');
    const lastDot = t.lastIndexOf('.');

    let normalized = t;

    if (lastComma !== -1 && lastDot !== -1) {
      normalized = lastComma > lastDot
        ? t.replace(/\./g, '').replace(',', '.')
        : t.replace(/,/g, '');
    } else if (lastComma !== -1) {
      normalized = t.replace(',', '.');
    }

    // eslint-disable-next-line no-restricted-globals
    const n = Number(normalized);

    return Number.isFinite(n) ? n : null;
  }

  private formatNumberForModel(n: number): string {
    // 2 decimals like "0,00"
    return n.toFixed(2).replace('.', ',');
  }

  // -----------------------------
  // Visibility safety
  // -----------------------------

  private safeIsVisible(sf: RepeatableSubField): boolean {
    if (typeof sf.isVisible !== 'function') return true;

    try {
      return sf.isVisible() !== false;
    } catch (err: unknown) {
      this.debugWarn('isVisible() threw; showing field by default.', { key: sf.key, err });
      return true;
    }
  }

  // -----------------------------
  // Snapshot utilities
  // -----------------------------

  private getRowsSnapshot(): RowValue[] {
    const rows: RowValue[] = [];

    for (let i = 0; i < this.rowsArray.length; i++) {
      rows.push({ ...(this.rowsArray.at(i).getRawValue() as RowValue) });
    }

    return rows;
  }

  private getRowsSnapshotAsStrings(): Array<Record<string, string>> {
    const rows: Array<Record<string, string>> = [];

    for (let i = 0; i < this.rowsArray.length; i++) {
      const raw = this.rowsArray.at(i).getRawValue() as RowValue;
      const out: Record<string, string> = {};

      for (const [k, v] of Object.entries(raw)) {
        out[k] = String(v ?? '');
      }

      rows.push(out);
    }

    return rows;
  }

  private splitSerializedKeepEmptiesAndSanitize(value: unknown, sep: string): string[] {
    return String(value ?? '')
      .split(sep)
      .map((x) => {
        const t = String(x ?? '').trim();

        if (!t || this.isNullishStringToken(t)) return '';

        return t;
      });
  }

  private areEqualLoose(a: unknown, b: unknown): boolean {
    return String(a ?? '') === String(b ?? '');
  }

  private isNumericLike(value: string): boolean {
    const s = value.trim();
    if (!s) return false;

    // eslint-disable-next-line no-restricted-globals
    return !Number.isNaN(Number(s));
  }

  private isNullishStringToken(value: string): boolean {
    return NULLISH_STRING_TOKENS.has(value.trim().toLowerCase());
  }

  private debugLog(message: string, data?: unknown): void {
    if (!this.debug) return;
    // eslint-disable-next-line no-console
    console.log(`[RepeatableGroup] ${message}`, data ?? '');
  }

  private debugWarn(message: string, data?: unknown): void {
    if (!this.debug) return;
    // eslint-disable-next-line no-console
    console.warn(`[RepeatableGroup] ${message}`, data ?? '');
  }
}
